import { useEffect, useState } from "react";
import { confirmAction } from "../../utils/confirm";
import AppModal from "../../components/AppModal";
import RichTextEditor from "../../components/RichTextEditor";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { handbookService } from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { showToast } from "../../utils/toast";
import { validateForm } from "../../utils/validate";
import { isRichTextEmpty, stripHtml } from "../../utils/richText";
import { ACTIVE_STATUS_FILTERS, emptyMessageForStatus, statusFromFilter } from "../../utils/listFilters";

const STATUS_FILTERS = ACTIVE_STATUS_FILTERS("All chapters");

const EMPTY = {
  id: null,
  chapter_no: "",
  title: "",
  body: "",
  sort_order: 0,
  is_active: 1,
  facts: [],
};

export default function Handbook() {
  const { can } = usePermission();
  const canAdd = can("handbook", "is_add");
  const canEdit = can("handbook", "is_update");
  const canDelete = can("handbook", "is_delete");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [bodyError, setBodyError] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = (params = {}) => {
    setLoading(true);
    handbookService
      .getAll({
        search: params.search ?? search,
        status: statusFromFilter(params.filter ?? viewFilter, STATUS_FILTERS),
      })
      .then((res) => setRows(res?.data?.list || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load({ filter: viewFilter, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    load({ filter: viewFilter, search });
  };

  const onReset = (e) => {
    e.preventDefault();
    setViewFilter("all");
    setSearch("");
    load({ filter: "all", search: "" });
  };

  const handleStatusChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const emptyMessage = emptyMessageForStatus(viewFilter, {
    all: "No handbook chapters yet",
    active: "No active chapters",
    inactive: "No inactive chapters",
  });

  const openAdd = () => {
    setForm(EMPTY);
    setBodyError(false);
    setModalOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY, ...row, facts: Array.isArray(row.facts) ? row.facts : [] });
    setBodyError(false);
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
  };

  const setFact = (i, key, val) =>
    setForm((f) => {
      const facts = [...f.facts];
      facts[i] = { ...facts[i], [key]: val };
      return { ...f, facts };
    });
  const addFact = () =>
    setForm((f) => ({ ...f, facts: [...f.facts, { k: "", v: "" }] }));
  const removeFact = (i) =>
    setForm((f) => ({ ...f, facts: f.facts.filter((_, idx) => idx !== i) }));

  const onBodyChange = (html) => {
    setForm((f) => ({ ...f, body: html }));
    if (bodyError && !isRichTextEmpty(html)) setBodyError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const bodyInvalid = isRichTextEmpty(form.body);
    setBodyError(bodyInvalid);
    if (
      !validateForm(e.currentTarget, [
        { name: "title", value: form.title, label: "Title", required: true },
      ]) ||
      bodyInvalid
    )
      return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        facts: form.facts.filter((x) => x.k || x.v),
      };
      const res = form.id
        ? await handbookService.update(payload)
        : await handbookService.create(payload);
      if (res?.success) {
        showToast("success", form.id ? "Chapter updated" : "Chapter created");
        setModalOpen(false);
        load({ filter: viewFilter, search });
      } else showToast("error", res?.msg || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const r = await confirmAction({
      title: `Delete "${row.title}"?`,
      icon: "warning",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!r.isConfirmed) return;
    const res = await handbookService.delete(row.id);
    if (res?.success) {
      showToast("success", "Chapter deleted");
      load({ filter: viewFilter, search });
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Parent Handbook</strong>
        {canAdd && (
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="cil-plus me-1" /> Add Chapter
          </button>
        )}
      </div>
      <div className="card-body">
        <p className="text-muted small">
          The handbook is universal (group-wide). Centre-specific details like hours,
          address and manager are surfaced from each family's account, not duplicated here.
        </p>

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id="hb-status-filter" label="Show">
            <select
              id="hb-status-filter"
              className="form-select"
              value={viewFilter}
              onChange={handleStatusChange}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="hb-search" label="Search">
            <input
              id="hb-search"
              className="form-control"
              placeholder="Chapter title or body…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        {loading && <p className="text-muted">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-muted">{emptyMessage}</p>
        )}

        <div className="row g-3">
          {rows.map((r) => (
            <div className="col-12 col-lg-6" key={r.id}>
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <h5 className="mb-1">
                      <span className="text-muted me-2">{r.chapter_no}</span>
                      {r.title}
                    </h5>
                    {r.is_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Hidden</span>
                    )}
                  </div>
                  <p className="text-muted small">{stripHtml(r.body).slice(0, 220)}{stripHtml(r.body).length > 220 ? "…" : ""}</p>
                  {Array.isArray(r.facts) && r.facts.length > 0 && (
                    <ul className="small mb-2">
                      {r.facts.map((f, i) => (
                        <li key={i}>
                          <strong>{f.k}:</strong> {f.v}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="d-flex gap-1">
                    {canEdit && (
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>Edit</button>
                    )}
                    {canDelete && (
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>Delete</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={form.id ? "Edit Chapter" : "Add Chapter"}
        size="lg"
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" form="hb-form" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : form.id ? "Save Changes" : "Create"}
            </button>
          </>
        }
      >
              <form id="hb-form" onSubmit={handleSubmit} noValidate>
                <div className="row g-3">
                  <div className="col-3">
                    <label className="form-label">Chapter #</label>
                    <input name="chapter_no" className="form-control" value={form.chapter_no || ""} onChange={onChange} placeholder="01" />
                  </div>
                  <div className="col-9">
                    <label className="form-label">Title <span className="text-danger">*</span></label>
                    <input name="title" className="form-control" value={form.title} onChange={onChange} required />
                  </div>
                </div>
                <div className="mb-3 mt-3">
                  <label className="form-label">Body <span className="text-danger">*</span></label>
                  <RichTextEditor
                    key={form.id || "new"}
                    value={form.body || ""}
                    onChange={onBodyChange}
                    invalid={bodyError}
                    placeholder="Write chapter content…"
                  />
                  {bodyError && (
                    <div className="invalid-feedback d-block">Body is required</div>
                  )}
                </div>

                <label className="form-label">Quick facts</label>
                {form.facts.map((f, i) => (
                  <div className="d-flex gap-2 mb-2" key={i}>
                    <input
                      className="form-control"
                      placeholder="Label"
                      value={f.k || ""}
                      onChange={(e) => setFact(i, "k", e.target.value)}
                    />
                    <input
                      className="form-control"
                      placeholder="Value"
                      value={f.v || ""}
                      onChange={(e) => setFact(i, "v", e.target.value)}
                    />
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeFact(i)} aria-label="Remove">
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={addFact}>
                  <i className="cil-plus me-1" /> Add fact
                </button>

                <div className="row g-3 align-items-center">
                  <div className="col-4">
                    <label className="form-label">Sort order</label>
                    <input name="sort_order" type="number" className="form-control" value={form.sort_order} onChange={onChange} />
                  </div>
                  <div className="col-8 pt-4">
                    <div className="form-check">
                      <input type="checkbox" id="hbActive" className="form-check-input" name="is_active" checked={!!form.is_active} onChange={onChange} />
                      <label htmlFor="hbActive" className="form-check-label ms-1">Active</label>
                    </div>
                  </div>
                </div>
              </form>
      </AppModal>
    </div>
  );
}
